import { ImageOff } from 'lucide-react';
import { useDocumentObjectUrl } from '../../apis/documents-http';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';

export interface DocumentImageProps {
  documentId: string;
  alt: string;
  className?: string;
  /** Tailwind classes for the frame; the default is the photo grid's square slot. */
  frameClassName?: string;
}

/**
 * A photograph, rendered from an **authenticated fetch** (plan §2.5).
 *
 * `<img src="/api/documents/…/content">` would be a plain browser request with no `Authorization`
 * header — a 401 and a broken image. The bytes are fetched with the session's bearer token and handed
 * to the DOM as a `blob:` object URL instead, which is why every `src` on the documents tab starts with
 * `blob:` and none of them is a link that works outside this tab.
 *
 * The skeleton and the error line are not decoration: a licence scan over a dealership's connection
 * takes a moment, and "the file could not be loaded" is a different situation from "there is no file",
 * which is what the empty slot says.
 */
export const DocumentImage = ({ documentId, alt, className, frameClassName }: DocumentImageProps) => {
  const { t } = useI18nTranslation();
  const { url, isLoading, error } = useDocumentObjectUrl(documentId);

  return (
    <div className={cn('relative aspect-[4/3] w-full overflow-hidden rounded-md border border-border bg-muted', frameClassName)}>
      {isLoading ? <div className="size-full animate-pulse bg-muted" /> : null}

      {error !== null ? (
        <div className="flex size-full flex-col items-center justify-center gap-1 p-3 text-center">
          <ImageOff className="size-5 text-muted-foreground" />
          <p className="text-xs text-destructive">{t('documents.previewFailed')}</p>
        </div>
      ) : null}

      {url !== null ? (
        <img
          src={url}
          alt={alt}
          className={cn('size-full object-cover', className)}
        />
      ) : null}
    </div>
  );
};
